import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Droplet,
  ChevronRight,
  Filter,
  Waves,
  Recycle,
  Package,
  CircleDot,
  ShieldCheck,
  Clock,
  Search,
  Zap,
  ArrowRight
} from 'lucide-react';
import img1 from "../../image2/filt.jpg";
import img2 from "../../image2/ro.jpeg";
import img3 from "../../image2/housing.webp";
import img4 from "../../image2/uv.jpeg";
import img5 from "../../image2/dpump.png";
import img6 from "../../image2/ufro.webp";
import img7 from "../../image2/multi.webp";
import img8 from "../../image2/stain.webp";
import img9 from "../../image2/vess.jpeg";
import { useNavigate } from 'react-router-dom';

const WaterSpares = () => {
  const [activeCategory, setActiveCategory] = useState('filtration');
  const navigate = useNavigate();

  const categories = [
    {
      id: 'filtration',
      title: 'Filtration Systems',
      icon: <Filter className="w-5 h-5" />,
      description: 'Advanced filtration components for optimal water quality',
      products: [
        {
          title: 'Premium Filter Element',
          description: 'High-performance filtration component for industrial water treatment systems.',
          image: img1,
          inStock: true,
          price: 'NPR',
          specs: 'Micron Rating: 1-100μm'
        },
        {
          title: 'Membrane Filter',
          description: 'Advanced membrane filtration system for ultra-pure water applications.',
          image: img2,
          inStock: true,
          price: 'NPR',
          specs: 'Pore Size: 0.01-0.1μm'
        },
        {
          title: 'Carbon Filter Housing',
          description: 'Durable housing unit for activated carbon filtration systems.',
          image:img3,
          inStock: true,
          price: 'NPR',
          specs: 'Material: SS316/UPVC'
        }
      ]
    },
    {
      id: 'treatment',
      title: 'Treatment Equipment',
      icon: <Waves className="w-5 h-5" />,
      description: 'Essential parts for water treatment systems',
      products: [
        {
          title: 'UV Lamp Assembly',
          description: 'Replacement UV lamps for water disinfection systems.',
          image: img4,
          inStock: true,
          price: 'NPR',
          specs: 'Life: 9000 hours'
        },
        {
          title: 'Dosing Pumps',
          description: 'Precision chemical dosing pump for water treatment processes.',
          image: img5,
          inStock: true,
          price: 'NPR',
          specs: 'Flow: 0-100 L/hr'
        },
        {
          title: 'RO & UF Control Pannels ',
          description: 'Advanced control system for treatment plant automation.',
          image: img6,
          inStock: true,
          price: 'NPR',
          specs: 'IP65 Rated'
        }
      ]
    },
    {
      id: 'recycling',
      title: 'Recycling Units',
      icon: <Recycle className="w-5 h-5" />,
      description: 'Components for waste water recycling',
      products: [
        {
          title: 'Multi Port Valves ',
          description: 'High-efficiency pump for water recovery systems.',
          image: img7,
          inStock: true,
          price: 'NPR',
          specs: 'Flow Rate: 20m³/hr'
        },
        {
          title: 'Top & Bottom Stainers ',
          description: 'Replacement components for recycled water storage systems.',
          image: img8,
          inStock: true,
          price: '$NPR',
          specs: 'HDPE/PP Material'
        },
        {
          title: 'FRL Vessels ',
          description: 'Water quality monitoring equipment for recycling systems.',
          image: img9,
          inStock: true,
          price: 'NPR',
          specs: 'Digital Display'
        }
      ]
    }
  ];

  const activeProducts = categories.find(cat => cat.id === activeCategory)?.products || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white">
      {/* Hero Section */}
      <motion.section 
        className="relative min-h-[400px] bg-gradient-to-r from-cyan-900 via-cyan-800 to-teal-800 text-white overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Animated Wave Background */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute inset-0"
              initial={{ backgroundPositionX: "0%" }}
              animate={{ backgroundPositionX: "100%" }}
              transition={{
                duration: 20 + i * 5,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 1200 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 24c120 0 120 90 240 90s120-90 240-90 120 90 240 90 120-90 240-90 120 90 240 90V0H0v24z' fill='%23ffffff'/%3E%3C/svg%3E\")",
                backgroundRepeat: "repeat-x",
                backgroundSize: "1200px 120px",
                top: `${i * 40}%`,
                opacity: 0.3 - i * 0.1
              }}
            />
          ))}
        </div>

        <div className="container mx-auto px-6 h-full flex flex-col justify-center relative z-10 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Droplet className="w-8 h-8 text-cyan-300" />
              <h1 className="text-4xl md:text-5xl font-bold">
                Water & Waste Spares
              </h1>
            </div>
            <p className="text-lg md:text-xl mb-6 max-w-2xl text-cyan-50">
              Complete range of high-quality spare parts for water treatment and waste management systems
            </p>
            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <button 
                className="bg-white text-cyan-900 px-6 py-2.5 rounded-lg font-semibold hover:bg-cyan-50 transition-all duration-300 flex items-center group"
                onClick={() => navigate('/products/water-management')}
              >
                Explore Products
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Quick Links */}
      <section className="py-12 px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: <ShieldCheck />, title: 'Quality Certified', text: 'ISO certified components' },
              { icon: <Clock />, title: 'Fast Delivery', text: '24-48 hour dispatch' },
              { icon: <Search />, title: 'Parts Lookup', text: 'Easy part identification' },
              { icon: <Zap />, title: 'Expert Support', text: 'Technical assistance' }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="text-cyan-600 mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-cyan-50">
        <div className="container mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
          >
            <h2 className="text-3xl font-bold mb-4">Spare Parts Categories</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Browse our comprehensive range of water and waste management spare parts
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-4 justify-center mb-12">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-cyan-700 text-white'
                    : 'bg-white text-gray-700 hover:bg-cyan-50'
                }`}
                onClick={() => setActiveCategory(category.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category.icon}
                {category.title}
              </motion.button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {activeProducts.map((product, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="relative">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm ${
                    product.inStock ? 'bg-cyan-600 text-white' : 'bg-gray-600 text-white'
                  }`}>
                    {product.inStock ? 'In Stock' : 'Out of Stock'}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-2">{product.title}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-cyan-600 font-bold">{product.price}</span>
                    <span className="text-gray-500 text-sm">{product.specs}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WaterSpares;